import { useState } from 'react';
import { Button, Snackbar, Alert, Tooltip } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { userApi } from '../../services/userApi';
import { useAuthStore } from '../../stores/authStore';

interface HideRetailerButtonProps {
  /** The Retailer id to hide. */
  retailerId: string;
  /** Retailer name for accessible naming. */
  retailerName: string;
  /** Compact icon-only rendering for dense rows. */
  iconOnly?: boolean;
}

/**
 * Per-retailer "Hide" toggle (issue #9 — hide / filter-out retailers). Mirrors
 * the FavoriteRetailerButton / WatchButton pattern: logged-out users are
 * routed to /login with a return path so they land back on the retailer page;
 * logged-in users toggle the retailer in their hidden set via POST/DELETE
 * /user/hidden-retailers. While hidden, the retailer is excluded from the
 * caller's own search results (the search service merges the hidden set into
 * its `excludedRetailers` user-context filter).
 *
 * The component owns its own hidden-retailers query so the toggled state
 * survives re-renders and reflects pre-existing hides on first load. Anonymous
 * users skip the query entirely.
 */
export function HideRetailerButton({
  retailerId,
  retailerName,
  iconOnly = false,
}: HideRetailerButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );

  const hiddenQuery = useQuery({
    queryKey: ['hidden-retailers'],
    queryFn: () => userApi.listHiddenRetailers(),
    enabled: isAuthenticated,
    retry: false,
  });

  const hiddenItem = hiddenQuery.data?.hidden.find((h) => h.retailerId === retailerId);
  const isHidden = Boolean(hiddenItem);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['hidden-retailers'] });

  const addMutation = useMutation({
    mutationFn: () => userApi.addHiddenRetailer(retailerId),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Hidden from your search results.' });
      void invalidate();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t hide retailer: ${err.message}` });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => userApi.removeHiddenRetailer(hiddenItem?.id as string),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Retailer is no longer hidden.' });
      void invalidate();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t un-hide retailer: ${err.message}` });
    },
  });

  const pending = addMutation.isPending || removeMutation.isPending;

  function onClick() {
    if (!isAuthenticated) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }
    if (isHidden) {
      removeMutation.mutate();
    } else {
      addMutation.mutate();
    }
  }

  const label = isHidden
    ? `Show ${retailerName} in search results`
    : `Hide ${retailerName} from search results`;
  const text = isHidden ? 'Hidden' : 'Hide';
  const icon = isHidden ? (
    <VisibilityOffIcon fontSize="small" />
  ) : (
    <VisibilityIcon fontSize="small" />
  );

  return (
    <>
      <Tooltip title={isAuthenticated ? label : 'Sign in to hide this retailer'}>
        <span>
          <Button
            size="small"
            variant={isHidden ? 'contained' : 'outlined'}
            color={isHidden ? 'primary' : 'inherit'}
            onClick={onClick}
            disabled={pending}
            aria-label={label}
            aria-pressed={isHidden}
            startIcon={iconOnly ? undefined : icon}
            sx={{ whiteSpace: 'nowrap', minWidth: iconOnly ? 0 : undefined }}
          >
            {iconOnly ? icon : text}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default HideRetailerButton;
